import { ReactNode } from 'react';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { startCase } from 'lodash-es';
import { PhoneCallsTypes } from '@frontend/api-phone-calls';
import { useTranslation, i18next, Trans } from '@frontend/i18n';
import { PhonesUtils } from '@frontend/phone-panels';
import { OUTCOME_MAPPING, getAiOutcomeIcon as getIcon } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Chip, Text, Button, PopoverDialog, usePopoverDialog, ChipVariants } from '@frontend/design-system';
import { recentCallsTrackingId } from '../../pendo-tracking';

export const AiCallCreditInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({});

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              AI Call Credit
            </Text>

            <Text size='medium'>
              Calls are counted toward your monthly call credit usage when the interaction with the AI Receptionist is
              at least 20 seconds and the caller engages in the conversation.
            </Text>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

export const AiOutcomeInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  // const { modalProps, triggerProps } = useModalControl();
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    // placement: 'top-start',
  });

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              AI Call Outcome
            </Text>

            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Determine if the AI Receptionist was able to handle the call on its own or if it needed to involve your
                office staff.
              </Text>
            </div>
            <div style={{ display: 'grid', gap: theme.spacing(1) }}>
              <Text size='medium' weight='bold'>
                How It's Defined
              </Text>
              <div style={{ display: 'grid', gap: theme.spacing(1) }}>
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_END_CALL]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_ROUTE_MISSED]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_FATAL]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_INCOMPLETE]} />
              </div>
            </div>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

export const ResultInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>();

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              Call Result
            </Text>

            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Assess how well incoming calls are being handled by your staff or by your AI Receptionist.
              </Text>
            </div>
            <div style={{ display: 'grid', gap: theme.spacing(1) }}>
              <div>
                <Text size='medium' weight='bold'>
                  How It's Defined
                </Text>
                <Text size='medium'>
                  The call result reflects the{' '}
                  <Text as='span' size='medium' weight='bold'>
                    final status
                  </Text>{' '}
                  of the call. For example, if the AI answers but your staff misses the transfer, the call result will
                  show as Missed.
                </Text>
              </div>
              <div style={{ display: 'grid', gap: theme.spacing(1), gridTemplateColumns: 'auto 1fr' }}>
                <ResultDisplay config={RESULT_CONFIG['answered']} />
                <ResultDisplay config={RESULT_CONFIG['missed']} />
                <ResultDisplay config={RESULT_CONFIG['abandoned']} />
                <ResultDisplay config={RESULT_CONFIG['forwarded']} />
              </div>
            </div>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

type OutcomeConfig = {
  outcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING];
  text: string;
  secondaryText?: ReactNode;
};

const AI_OUTCOME_CONFIG: Record<string, OutcomeConfig> = {
  [AIOutcome.AI_OUTCOME_END_CALL]: {
    outcome: OUTCOME_MAPPING.SUCCESS,
    text: i18next.t('Successfully assisted the patient: ', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist successfully handled the call on its own.', { ns: 'calls' }),
  },
  [AIOutcome.AI_OUTCOME_FATAL]: {
    outcome: OUTCOME_MAPPING.FATAL,
    text: i18next.t('Error: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist{' '}
        <Text as='span' weight='bold' size='medium'>
          could not answer
        </Text>{' '}
        the call or{' '}
        <Text as='span' weight='bold' size='medium'>
          could not finish
        </Text>{' '}
        assisting the caller. This can happen when the set up is incomplete or when the system is experiencing a
        temporary disruption.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: {
    outcome: OUTCOME_MAPPING.ROUTE_MISSED,
    text: i18next.t('Needs attention: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist transferred the call to your office, but the{' '}
        <Text as='span' weight='bold' size='medium'>
          transfer was missed
        </Text>{' '}
        . The caller may still need assistance.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: {
    outcome: OUTCOME_MAPPING.ROUTE_ANSWERED,
    text: i18next.t('Call transferred: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist{' '}
        <Text as='span' weight='bold' size='medium'>
          handed the conversation off{' '}
        </Text>
        for further assistance, either by transferring the caller to office staff or by moving the conversation to text.
        This can happen when the request isn’t supported or when the caller asks to speak with staff.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_INCOMPLETE]: {
    outcome: OUTCOME_MAPPING.INCOMPLETE,
    text: i18next.t('Incomplete: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The caller{' '}
        <Text as='span' weight='bold' size='medium'>
          hung up{' '}
        </Text>
        before the AI Receptionist could determine the outcome.
      </Trans>
    ),
  },
};

const OutcomeDisplay = ({ config }: { config: OutcomeConfig }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    }}
  >
    <div style={{ justifySelf: 'end', alignSelf: 'start', display: 'grid' }}>
      {getIcon({ outcome: config.outcome })}
    </div>
    <div>
      <Text as='span' size='medium' weight='bold'>
        {config.text}
      </Text>
      {config.secondaryText && (
        <Text as='span' size='medium'>
          {config.secondaryText}
        </Text>
      )}
    </div>
  </div>
);

type ResultConfig = {
  result: PhoneCallsTypes.CallLog['Status'];
  text: string;
};

const RESULT_CONFIG: Record<PhoneCallsTypes.CallLog['Status'], ResultConfig> = {
  answered: {
    result: 'answered',
    text: i18next.t('The call was answered by your staff or the AI Receptionist. ', { ns: 'calls' }),
  },
  missed: {
    result: 'missed',
    text: i18next.t('The call was not answered by your staff or by the AI Receptionist, and it went to voicemail.', {
      ns: 'calls',
    }),
  },
  abandoned: {
    result: 'abandoned',
    text: i18next.t('The caller hung up before the call could be answered or go to voicemail.', { ns: 'calls' }),
  },
  forwarded: {
    result: 'forwarded',
    text: i18next.t('The call was forwarded externally to another phone number, but the call was not answered.', {
      ns: 'calls',
    }),
  },
};

const ResultDisplay = ({ config }: { config: ResultConfig }) => {
  const statusChip = PhonesUtils.getStatusChipColor(config.result);

  return (
    <div
      style={{
        display: 'grid',
        gridColumn: 'span 3',
        gridTemplateColumns: 'subgrid',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Chip isResponsive variant={statusChip} css={{ justifySelf: 'start', alignSelf: 'start', display: 'grid' }}>
        {startCase(config.result)}
      </Chip>
      <Text as='span' size='medium'>
        {config.text}
      </Text>
    </div>
  );
};

type PreciseResultRow = {
  label: string;
  variant: ChipVariants;
  description: ReactNode;
};

type PreciseResultSection = {
  title: string;
  rows: PreciseResultRow[];
};

const getPreciseResultSectionsAirEnabled = (): PreciseResultSection[] => [
  {
    title: i18next.t('Inbound Calls', { ns: 'calls' }),
    rows: [
      {
        label: i18next.t('Answered', { ns: 'calls' }),
        variant: 'primary',
        description: i18next.t('The call was answered by a Weave Phone or fully handled by the AI Receptionist.', {
          ns: 'calls',
        }),
      },
      {
        label: i18next.t('Missed', { ns: 'calls' }),
        variant: 'critical',
        description: i18next.t('The call was destined for a Weave phone, but was not answered.', { ns: 'calls' }),
      },
      {
        label: i18next.t('Sent to Voicemail', { ns: 'calls' }),
        variant: 'primary',
        description: i18next.t(
          'The call was routed straight to voicemail (no phones rang) and the caller left a message.',
          { ns: 'calls' }
        ),
      },
      {
        label: i18next.t('Sent to Play Message', { ns: 'calls' }),
        variant: 'primary',
        description: i18next.t(
          'The call was routed straight to a recorded message, and the caller listened to the whole message.',
          { ns: 'calls' }
        ),
      },
      {
        label: i18next.t('Abandoned', { ns: 'calls' }),
        variant: 'warn',
        description: i18next.t(
          'The caller hung up before reaching a Weave phone, conversing with the AI Receptionist, or leaving a voicemail message.',
          { ns: 'calls' }
        ),
      },
    ],
  },
  {
    title: i18next.t('Forwarded Calls', { ns: 'calls' }),
    rows: [
      {
        label: i18next.t('Forwarded + Connected', { ns: 'calls' }),
        variant: 'primary',
        description: i18next.t('The forwarded call connected successfully.', { ns: 'calls' }),
      },
      {
        label: i18next.t('Forwarded + Not Connected', { ns: 'calls' }),
        variant: 'critical',
        description: i18next.t('The forwarded call failed to connect.', { ns: 'calls' }),
      },
      {
        label: i18next.t('Forwarded + Abandoned', { ns: 'calls' }),
        variant: 'warn',
        description: i18next.t(
          'The call is forwarded and the caller hangs up (cancels) the call before the call connects.',
          { ns: 'calls' }
        ),
      },
    ],
  },
  {
    title: i18next.t('Outbound Calls', { ns: 'calls' }),
    rows: [
      {
        label: i18next.t('Connected', { ns: 'calls' }),
        variant: 'primary',
        description: i18next.t('The call connected successfully.', { ns: 'calls' }),
      },
      {
        label: i18next.t('Not Connected', { ns: 'calls' }),
        variant: 'warn',
        description: i18next.t('The call failed to connect.', { ns: 'calls' }),
      },
      {
        label: i18next.t('Canceled', { ns: 'calls' }),
        variant: 'neutral',
        description: i18next.t('The call was hung up by the Weave Phone prior to being connected.', { ns: 'calls' }),
      },
    ],
  },
  {
    title: i18next.t('Routing and System Issues', { ns: 'calls' }),
    rows: [
      {
        label: i18next.t('Call Terminated', { ns: 'calls' }),
        variant: 'critical',
        description: i18next.t(
          'The call reached a dead end in the routing setup (e.g., a phone tree option with no destination) and Weave ended it automatically.',
          { ns: 'calls' }
        ),
      },
      {
        label: i18next.t('System Failure', { ns: 'calls' }),
        variant: 'critical',
        description: i18next.t(
          'The call was terminated due to an incomplete call route configuration. Review your call routing if you see this result.',
          { ns: 'calls' }
        ),
      },
    ],
  },
];

// AIR-disabled copy differs only in the intro text plus the Answered and Abandoned descriptions.
const getPreciseResultSectionsAirDisabled = (): PreciseResultSection[] =>
  getPreciseResultSectionsAirEnabled().map((section) => {
    if (section.title !== i18next.t('Inbound Calls', { ns: 'calls' })) return section;
    return {
      ...section,
      rows: section.rows.map((row) => {
        if (row.label === i18next.t('Answered', { ns: 'calls' })) {
          return { ...row, description: i18next.t('The call was answered by a Weave Phone.', { ns: 'calls' }) };
        }
        if (row.label === i18next.t('Abandoned', { ns: 'calls' })) {
          return {
            ...row,
            description: i18next.t('The caller hung up before reaching a Weave phone or leaving a voicemail message.', {
              ns: 'calls',
            }),
          };
        }
        return row;
      }),
    };
  });

const PreciseResultRowDisplay = ({ row }: { row: PreciseResultRow }) => (
  <div
    style={{
      display: 'grid',
      gridColumn: '1 / -1',
      gridTemplateColumns: 'subgrid',
      alignItems: 'start',
      gap: theme.spacing(1),
    }}
  >
    <Chip variant={row.variant} maxWidth='max-content' css={{ justifySelf: 'start' }}>
      {row.label}
    </Chip>
    <Text as='span' size='medium'>
      {row.description}
    </Text>
  </div>
);

export const PreciseResultInfoModalTrigger = ({ isAirEnabled }: { isAirEnabled: boolean }) => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>();
  const sections = isAirEnabled ? getPreciseResultSectionsAirEnabled() : getPreciseResultSectionsAirDisabled();

  return (
    <>
      <Button
        {...getTriggerProps()}
        iconName='info'
        variant='secondary'
        css={{ padding: 0, height: 16, width: 16 }}
        trackingId={recentCallsTrackingId({ context: 'precise-results-info::click' })}
      />
      <PopoverDialog {...getDialogProps()} css={{ maxHeight: '75vh' }}>
        <div style={{ display: 'grid', gap: theme.spacing(2), width: 393 }}>
          <Text size='large' weight='bold'>
            {t('Precise Call Results')}
          </Text>

          <div>
            <Text size='medium' weight='bold'>
              {t('What They Help With')}
            </Text>
            <Text size='medium'>
              {isAirEnabled
                ? t(
                    'See how calls are being properly or improperly resolved by staff, the AI receptionist, and your phone system.'
                  )
                : t('See how calls are being properly or improperly resolved by staff and your phone system.')}
            </Text>
          </div>

          <div>
            <Text size='medium' weight='bold'>
              {t("How They're Defined")}
            </Text>
            <Text size='medium'>
              {t(
                'The call result reflects the final status of the call. To see the full journey of the call, go to Call Path in the Call Insights panel.'
              )}
            </Text>
          </div>

          {sections.map((section) => (
            <div key={section.title} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(1) }}>
              <Text size='medium' weight='bold' css={{ gridColumn: '1 / -1' }}>
                {section.title}
              </Text>
              {section.rows.map((row) => (
                <PreciseResultRowDisplay key={row.label} row={row} />
              ))}
            </div>
          ))}
        </div>
      </PopoverDialog>
    </>
  );
};
